import httpService from "../../utils/httpService"
import {createSlice, PayloadAction} from "@reduxjs/toolkit"
import {AppThunk} from "../../rootReducer"
import {getAccessToken, getSelectedRegionId} from "../../utils/common"
import {setToastr} from "../App/appSlice"
import {fetchCustomerContactReminderList} from "../MotBooking/bookingSlice"

export type Customer = {
    customerId: number
    regionId: number
    accountNo: string
    companyName: string
    address1: string
    address2: string
    address3: string
    city: string
    postCode: string
    telephone1: string
    telephone2: string
    email: string
    web: string
    contactName: string
    source: string
    active: boolean
}
export type CustomerContact = {
    customerId: number
    customerContactId: number
    firstName: string
    lastName: string
    email: string
    phone: string
    primaryContact: boolean
    active: boolean
    inEdit: boolean
    reminderEmail: boolean
}

type CustomerState = {
    customerSearchText: string
    loading: boolean
    customers: Customer[]
    customerContactloading: boolean
    customerContacts: CustomerContact[]
    togggleAddEditModal: boolean
    error: string
    customer: Customer
    customerDetailloading: boolean
    addEditOpen: boolean
    selectedCustomer: Customer
    showDeletePopover: boolean
    customerContacId: number
    // showSuccessToastr: boolean
    // showErrorToastr: boolean
}

export const customerInit: Customer = {
    customerId: 0,
    regionId: 0,
    accountNo: "",
    companyName: "",
    address1: "",
    address2: "",
    address3: "",
    city: "",
    postCode: "",
    telephone1: "",
    telephone2: "",
    email: "",
    web: "",
    contactName: "",
    source: "",
    active: false,
}
const initialState: CustomerState = {
    customerSearchText: "",
    loading: false,
    customers: [],
    customerContactloading: false,
    customerContacts: [],
    togggleAddEditModal: false,
    error: "",
    customer: customerInit,
    customerDetailloading: false,
    addEditOpen: false,
    selectedCustomer: customerInit,
    showDeletePopover: false,
    customerContacId: 0,
    // showSuccessToastr: false,
    // showErrorToastr: false,
}

const customerSlice = createSlice({
    name: "customer",
    initialState,
    reducers: {
        setCustomerSearchText(state, action: PayloadAction<string>) {
            state.customerSearchText = action.payload
        },
        showLoader(state, action: PayloadAction<boolean>) {
            state.loading = action.payload
        },
        setCustomers(state, action: PayloadAction<any[]>) {
            state.customers = action.payload
        },
        showCustomerDetailLoader(state, action: PayloadAction<boolean>) {
            state.customerDetailloading = action.payload
        },
        setCustomerDetail(state, action: PayloadAction<Customer>) {
            state.customer = action.payload
        },
        showCustomerContactLoader(state, action: PayloadAction<boolean>) {
            state.customerContactloading = action.payload
        },
        setCustomerContact(state, action: PayloadAction<CustomerContact[]>) {
            state.customerContacts = action.payload
        },
        toggleAddEdit(state, action: PayloadAction<boolean>) {
            state.addEditOpen = action.payload
        },
        setShowDeletePopover(state, action: PayloadAction<boolean>) {
            state.showDeletePopover = action.payload
        },
        setSelectedCustomer(state, action: PayloadAction<Customer>) {
            state.selectedCustomer = action.payload
        },
        setDeleteCustomercontactId(state, action: PayloadAction<number>) {
            state.customerContacId = action.payload
        },
        // showSuccessToastr(state, action: PayloadAction<boolean>) {
        //     state.showSuccessToastr = action.payload
        // },
        // showErrorToastr(state, action: PayloadAction<boolean>) {
        //     state.showErrorToastr = action.payload
        // },
    },
})

export const {
    setCustomerSearchText,
    setCustomers,
    showLoader,
    showCustomerContactLoader,
    setCustomerContact,
    showCustomerDetailLoader,
    setCustomerDetail,
    toggleAddEdit,
    setSelectedCustomer,
    setShowDeletePopover,
    setDeleteCustomercontactId,
    // showSuccessToastr,
    // showErrorToastr,
} = customerSlice.actions

export default customerSlice.reducer

// #region Thunks
export const customerSearchText =
    (filterText: string): AppThunk =>
    dispatch => {
        dispatch(setCustomerSearchText(filterText))
    }

export const fetchCustomers =
    (filterText: string): AppThunk =>
    (dispatch, getState) => {
        dispatch(showLoader(true))
        httpService(getAccessToken(getState()))
            .get(
                `regions/${getSelectedRegionId(getState())}/customers/longsearch?searchtext=${encodeURIComponent(
                    filterText,
                )}`,
            )
            .then(res => {
                dispatch(showLoader(false))
                const customers = res.data
                dispatch(setCustomers(customers))
            })
            .catch(error => {
                dispatch(showLoader(false))
                dispatch(setCustomers([]))
            })
    }

export const addCustomer =
    (customerDetail: Customer): AppThunk =>
    (dispatch, getState) => {
        dispatch(showCustomerDetailLoader(true))
        customerDetail.regionId = getSelectedRegionId(getState())
        httpService(getAccessToken(getState()))
            .post(`regions/${getSelectedRegionId(getState())}/customers`, customerDetail)
            .then(result => {
                dispatch(showCustomerDetailLoader(false))
                dispatch(fetchCustomerDetail(result.data))
                dispatch(fetchCustomerContact(result.data))
                const filterText = getState().containers.customer.customerSearchText
                dispatch(fetchCustomers(filterText))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
            })
            .catch(error => {
                dispatch(showCustomerDetailLoader(false))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: error.response.data.Value.Error,
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
    }

export const editCustomer =
    (customerDetail: Customer): AppThunk =>
    (dispatch, getState) => {
        dispatch(showCustomerDetailLoader(true))
        customerDetail.regionId = getSelectedRegionId(getState())
        httpService(getAccessToken(getState()))
            .put(`regions/${getSelectedRegionId(getState())}/customers`, customerDetail)
            .then(result => {
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "",
                        delay: 3000,
                        title: "Success",
                        type: "light",
                        description: "",
                    }),
                )
                dispatch(showCustomerDetailLoader(false))
                dispatch(fetchCustomerDetail(result.data))
                dispatch(fetchCustomerContact(result.data))
                const filterText = getState().containers.customer.customerSearchText
                dispatch(fetchCustomers(filterText))
            })
            .catch(error => {
                dispatch(showCustomerDetailLoader(false))
                dispatch(
                    setToastr({
                        isActive: true,
                        message: "Failed",
                        delay: 3000,
                        title: "Error",
                        type: "danger",
                        description: "",
                    }),
                )
            })
    }

export const fetchCustomerDetail =
    (selectedCustomerId: any): AppThunk =>
    (dispatch, getState) => {
        dispatch(showCustomerDetailLoader(true))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/customers/${selectedCustomerId}`)
            .then(res => {
                const item = res.data as Customer
                const customerDetail = {
                    customerId: item.customerId,
                    accountNo: item.accountNo,
                    active: true,
                    address1: item.address1 ?? "",
                    address2: item.address2 ?? "",
                    address3: item.address3 ?? "",
                    city: item.city ?? "",
                    companyName: item.companyName ?? "",
                    contactName: item.contactName ?? "",
                    email: item.email ?? "",
                    postCode: item.postCode ?? "",
                    regionId: item.regionId,
                    telephone1: item.telephone1 ?? "",
                    telephone2: item.telephone2 ?? "",
                    web: item.web ?? "",
                    source: item.source ?? "",
                }
                dispatch(setCustomerDetail(customerDetail))
                setTimeout(() => {
                    dispatch(showCustomerDetailLoader(false))
                }, 500)
            })
            .catch(error => {})
    }

export const fetchCustomerContact =
    (selectedCustomerId: any): AppThunk =>
    (dispatch, getState) => {
        dispatch(showCustomerContactLoader(true))
        httpService(getAccessToken(getState()))
            .get(`regions/${getSelectedRegionId(getState())}/customers/${selectedCustomerId}/customercontacts`)
            .then(res => {
                dispatch(showCustomerContactLoader(false))
                const customerContacts = res.data
                dispatch(setCustomerContact(customerContacts))
            })
            .catch(error => {
                dispatch(showCustomerContactLoader(false))
                dispatch(setCustomerContact([]))
            })
    }

export const addCustomerContact =
    (customerContact: CustomerContact): AppThunk =>
    (dispatch, getState) => {
        dispatch(showCustomerContactLoader(true))
        httpService(getAccessToken(getState()))
            .post(
                `regions/${getSelectedRegionId(getState())}/customers/${customerContact.customerId}/customercontacts`,
                customerContact,
            )
            .then(result => {
                dispatch(showCustomerContactLoader(false))
                dispatch(fetchCustomerContact(customerContact.customerId))
            })
            .catch(error => {
                dispatch(showCustomerContactLoader(false))
            })
    }

export const editCustomerContact =
    (customerContact: CustomerContact): AppThunk =>
    (dispatch, getState) => {
        const customerContactDetails = {
            ...customerContact,
            firstName: customerContact.firstName.trim(),
            lastName: customerContact.lastName.trim(),
        }
        dispatch(showCustomerContactLoader(true))
        httpService(getAccessToken(getState()))
            .put(
                `regions/${getSelectedRegionId(getState())}/customers/${customerContact.customerId}/customercontacts`,
                customerContactDetails,
            )
            .then(result => {
                dispatch(showCustomerContactLoader(false))
                dispatch(fetchCustomerContact(customerContact.customerId))
            })
            .catch(error => {
                dispatch(showCustomerContactLoader(false))
            })
    }
export const deleteCustomerContact =
    (customerId: number, customerContactId: number): AppThunk =>
    (dispatch, getState) => {
        httpService(getAccessToken(getState()))
            .delete(`regions/${getSelectedRegionId(getState())}/customers/customercontacts/${customerContactId}`)
            .then(result => {
                dispatch(fetchCustomerContact(customerId))
                dispatch(setDeleteCustomercontactId(0))
                if (window.location.pathname !== "/customers") {
                    const bookingDetail = getState().containers.motBooking.bookingDetail
                    dispatch(fetchCustomerContactReminderList(bookingDetail, customerId))
                }
            })
            .catch(error => {})
    }
